// What marketing brought in, worked out from what each part reports. Pure,
// so the Performance tabs, Home's tiles and their tests all count the same
// way.
//
// Until bookings record where they came from (docs/PERFORMANCE_BACKEND.md),
// each source counts its own bookings: an email or text by the bookings its
// links led to, the front desk by the conversations where Bailey booked,
// a journey by the bookings the server put against it.
import moment from 'moment'

const DAY = 864e5
export const endOfDay = (d) => { const e = new Date(d); e.setHours(23, 59, 59, 999); return e.getTime() }
const time = (at) => { const t = at ? new Date(at).getTime() : NaN; return Number.isNaN(t) ? null : t }

// A range is { start, end } (Dates; null means no bound)
// What Performance and Home open on: the same 28 days the date picker's
// "Last 4 weeks" gives, so picking it again changes nothing
export const lastFourWeeks = (now = new Date()) => {
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return { start: new Date(end.getFullYear(), end.getMonth(), end.getDate() - 27), end }
}
export const inRange = (at, range) => {
  const t = time(at)
  if (t == null) return false
  if (range && range.start && t < range.start.getTime()) return false
  if (range && range.end && t > endOfDay(range.end)) return false
  return true
}
export const previousRange = (range) => {
  if (!range || !range.start || !range.end) return null
  const length = endOfDay(range.end) - range.start.getTime()
  const end = new Date(range.start.getTime() - 1)
  return { start: new Date(end.getTime() - length), end }
}
// A share to one decimal place, or null when there's nothing to divide by
export const rate = (num, den) => (den ? Math.round((num / den) * 1000) / 10 : null)
const sum = (list, f) => list.reduce((n, x) => n + (Number(f(x)) || 0), 0)

// ─── Emails and texts ───
// A sent email or text as every tab reads it
export const sendsFromApi = (rows, kind) => (rows || [])
  .filter(c => c && c.status === 'sent' && c.sentDate)
  .map(c => ({
    id: String(c.id),
    kind,
    name: c.name || (kind === 'sms' ? 'Text' : 'Email'),
    at: new Date(c.sentDate),
    stats: c.stats || {},
  }))

export const funnel = (sends) => {
  const sent = sum(sends, s => s.stats.sent)
  const delivered = sum(sends, s => s.stats.delivered)
  const clicked = sum(sends, s => s.stats.clicked)
  return {
    count: sends.length,
    sent,
    delivered,
    opened: sum(sends, s => s.stats.opened),
    clicked,
    bookings: sum(sends, s => s.stats.bookings),
    deliveryRate: rate(delivered, sent),
    clickRate: rate(clicked, delivered),
  }
}

// The emails and texts each campaign made ({ [sendId]: campaign })
export const campaignOfSend = (groups) => {
  const out = {}
  ;(groups || []).forEach((g) => {
    Object.values(g.created || {}).forEach((x) => {
      if (x && (x.channel === 'email' || x.channel === 'sms') && x.id) out[String(x.id)] = g
    })
  })
  return out
}

// ─── Campaigns ───
const overlaps = (g, range) => {
  const a = g.start ? new Date(g.start) : null
  const b = g.end ? new Date(g.end) : a
  if (!a) return false
  if (range.end && a.getTime() > endOfDay(range.end)) return false
  if (range.start && b && endOfDay(b) < range.start.getTime()) return false
  return true
}
const COUNTED = ['post', 'story', 'email', 'sms']

// Each approved campaign that ran in the range: what was in it, who its
// emails and texts sent in the range reached, the bookings they led to (so
// a campaign never counts more than the Overview does), and posts shared
export const campaignResults = (groups, sends, posts, range) => {
  const byId = {}
  ;(sends || []).forEach((s) => { byId[s.id] = s })
  const postsById = {}
  ;(posts || []).forEach((p) => { if (p && p.id) postsById[p.id] = p })
  return (groups || [])
    .filter(g => g && (g.status === 'approved' || g.status === 'cancelled') && Object.keys(g.created || {}).length && overlaps(g, range))
    .map((g) => {
      const made = Object.values(g.created || {}).filter(Boolean)
      const own = made.filter(x => x.channel === 'email' || x.channel === 'sms').map(x => byId[String(x.id)]).filter(s => s && inRange(s.at, range))
      const madePosts = made.filter(x => x.channel === 'post' || x.channel === 'story')
      const shared = madePosts.filter(x => postsById[x.id] && postsById[x.id].status === 'posted').length
      const items = {}
      ;(g.items || []).filter(i => i.on || i.cancelled).forEach((i) => { if (COUNTED.includes(i.channel)) items[i.channel] = (items[i.channel] || 0) + 1 })
      return {
        id: g.id,
        name: g.name || 'Campaign',
        start: g.start,
        end: g.end,
        by: g.planKey ? 'bailey' : 'yours',
        status: g.status,
        items,
        reached: own.length ? Math.max(...own.map(s => Number(s.stats.delivered) || 0)) : 0,
        tapped: sum(own, s => s.stats.clicked),
        bookings: sum(own, s => s.stats.bookings),
        posts: madePosts.length,
        shared,
        sends: own.length,
      }
    })
    .sort((a, b) => (b.bookings - a.bookings) || String(b.start || '').localeCompare(String(a.start || '')))
}

// ─── Social ───
const weekStart = (d) => moment(d).startOf('isoWeek')
export const socialResults = (posts, range, now = new Date()) => {
  const list = (posts || []).filter(Boolean)
  const shared = list.filter(p => p.status === 'posted' && inRange(p.postedAt || p.plannedAt || p.updatedAt, range))
  // Planned for a time that's come, so it could have been shared by now
  const due = list.filter(p => p.plannedAt && (p.status === 'planned' || p.status === 'posted') && inRange(p.plannedAt, range) && new Date(p.plannedAt) <= now)
  const byType = {}
  shared.forEach((p) => { byType[p.type] = (byType[p.type] || 0) + 1 })
  // The last eight weeks, and how many in a row had a post (a week still
  // under way doesn't break the run)
  const postedWeeks = new Set(list.filter(p => p.status === 'posted' && (p.postedAt || p.plannedAt)).map(p => weekStart(p.postedAt || p.plannedAt).format('YYYY-MM-DD')))
  const thisWeek = weekStart(now)
  const weeks = Array.from({ length: 8 }, (_, i) => {
    const w = moment(thisWeek).subtract(7 - i, 'weeks')
    return { start: w.format('YYYY-MM-DD'), label: w.format('D MMM'), posted: postedWeeks.has(w.format('YYYY-MM-DD')) }
  })
  let streak = 0
  let w = postedWeeks.has(thisWeek.format('YYYY-MM-DD')) ? moment(thisWeek) : moment(thisWeek).subtract(1, 'week')
  while (postedWeeks.has(w.format('YYYY-MM-DD')) && streak < 520) { streak += 1; w = w.subtract(1, 'week') }
  // Free times posts whose day has passed, to check against the diary
  const freeTimes = shared
    .filter(p => (p.type === 'freeSlots' || p.type === 'oneSlot') && p.content && p.content.date && moment(p.content.date).isBefore(now, 'day'))
    .map(p => ({
      id: p.id,
      date: p.content.date,
      times: p.type === 'oneSlot' ? [p.content.time].filter(Boolean) : (p.content.times || []).filter(Boolean),
      staffId: p.content.staffId || null,
    }))
    .filter(x => x.times.length)
  return { shared: shared.length, due: due.length, sharedOfDue: due.filter(p => p.status === 'posted').length, byType, weeks, streak, freeTimes }
}

// ─── Journeys ───
// A run as journey/runs/list returns it: startedAt, finishedAt, the booking
// the server put against it (attributedBookingId, attributedAt) and the
// history of its steps, each sent, deferred or skipped
const words = s => String(s || '').toLowerCase().split(/[^a-z]+/)
// A run that led to a booking, or ended on the journey's goal ("no_booking"
// and "goal_not_met" don't count)
export const isBooked = (r) => {
  if (!r) return false
  if (r.attributedBookingId) return true
  const w = words(r.statusReason)
  return r.status !== 'active' && !w.some(x => x === 'no' || x === 'not') && w.some(x => x === 'booked' || x === 'goal' || x === 'converted')
}
const bookedAt = r => r.attributedAt || r.finishedAt || r.updatedAt || null
const sentIn = (r, range) => (r.history || []).filter(h => h && h.outcome === 'sent' && inRange(h.at, range)).length
export const journeyResults = (journeys, runsById, range) => (journeys || []).map((j) => {
  const runs = (runsById && runsById[j.id]) || []
  const booked = runs.filter(isBooked).map(bookedAt).filter(at => inRange(at, range))
  return {
    id: j.id,
    name: j.name || 'Journey',
    live: j.status === 'ready',
    entered: runs.filter(r => inRange(r.startedAt || r.createdAt, range)).length,
    active: runs.filter(r => r.status === 'active').length,
    booked: booked.length,
    bookedAt: booked,
    messages: sum(runs, r => sentIn(r, range)),
  }
}).sort((a, b) => (b.booked - a.booked) || (b.entered - a.entered))

// ─── The front desk ───
const partOfDay = h => (h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening')
export const frontDeskResults = (conversations, range) => {
  const list = (conversations || []).filter(c => c && inRange(c.startedAt || c.at, range))
  const outcomes = { booked: 0, link: 0, blocked: 0, message: 0, transfer: 0, answered: 0 }
  list.forEach((c) => { if (c.outcome && outcomes[c.outcome] !== undefined) outcomes[c.outcome] += 1 })
  const when = {}
  list.forEach((c) => {
    const m = moment(c.startedAt || c.at)
    const k = `${m.format('dddd')} ${partOfDay(m.hours())}`
    when[k] = (when[k] || 0) + 1
  })
  const busiest = Object.keys(when).sort((a, b) => when[b] - when[a])[0] || null
  return {
    total: list.length,
    chats: list.filter(c => c.channel !== 'phone').length,
    calls: list.filter(c => c.channel === 'phone').length,
    outcomes,
    bookings: outcomes.booked,
    busiest: busiest && when[busiest] > 1 ? busiest : null,
    bookedAt: list.filter(c => c.outcome === 'booked').map(c => c.startedAt || c.at),
  }
}

// ─── Reviews and vouchers ───
export const reviewResults = (reviews, range) => {
  const list = (reviews || []).filter(r => r && inRange(r.at, range))
  return {
    count: list.length,
    average: list.length ? Math.round((sum(list, r => r.rating) / list.length) * 10) / 10 : null,
    google: list.filter(r => r.google).length,
  }
}

export const voucherResults = (dashboard) => {
  if (!dashboard) return null
  const cur = dashboard.current || {}
  const prev = dashboard.previous || null
  const channel = id => ((dashboard.channels || []).find(c => c && c._id === id) || { count: 0, revenue: 0 })
  return {
    count: Number(cur.count) || 0,
    revenue: Number(cur.revenue) || 0,
    redeemed: Number(cur.redeemed) || 0,
    outstanding: Number(cur.outstanding) || 0,
    online: channel('online'),
    pos: channel('pos'),
    previous: prev ? { count: Number(prev.count) || 0, revenue: Number(prev.revenue) || 0 } : null,
  }
}

// ─── The overview ───
export const SOURCES = [
  { id: 'campaigns', label: 'Campaigns' },
  { id: 'sends', label: 'Emails and texts' },
  { id: 'frontdesk', label: 'Front desk' },
  { id: 'journeys', label: 'Journeys' },
]

// Bookings by where they came from, for a range. `known` says which sources
// could be counted, so a source that can't isn't shown as zero. Without
// campaigns (`groups` null) every email and text counts on its own.
export const bookingsBySource = ({ sends = [], groups = null, frontDesk = null, journeys = null }, range) => {
  const inCampaign = campaignOfSend(groups)
  const inR = sends.filter(s => inRange(s.at, range))
  const by = { sends: sum(inR.filter(s => !inCampaign[s.id]), s => s.stats.bookings) }
  const known = ['sends']
  if (groups) { by.campaigns = sum(inR.filter(s => inCampaign[s.id]), s => s.stats.bookings); known.unshift('campaigns') }
  if (frontDesk) { by.frontdesk = frontDeskResults(frontDesk, range).bookings; known.push('frontdesk') }
  if (journeys) { by.journeys = sum(journeyResults(journeys.list, journeys.runs, range), j => j.booked); known.push('journeys') }
  return { by, known, total: known.reduce((n, k) => n + (by[k] || 0), 0) }
}

// Bookings week by week (month by month over long ranges, the latest 26),
// by source. Emails and texts sit in the week they were sent.
export const bookingsOverTime = ({ sends = [], groups = null, frontDesk = null, journeys = null }, range, now = new Date()) => {
  const end = range.end ? endOfDay(range.end) : now.getTime()
  const start = range.start ? range.start.getTime() : end - 28 * DAY
  const weekly = (end - start) / DAY <= 70
  const unit = weekly ? 'week' : 'month'
  const startOf = t => moment(t).startOf(weekly ? 'isoWeek' : 'month')
  const overYear = (end - start) / DAY > 366
  const buckets = []
  const cur = moment.max(startOf(start), startOf(end).subtract(25, unit))
  while (cur.valueOf() <= end) {
    const from = cur.valueOf()
    const next = moment(cur).add(1, unit)
    buckets.push({ from, to: next.valueOf(), label: cur.format(weekly ? 'D MMM' : overYear ? 'MMM YY' : 'MMM'), campaigns: 0, sends: 0, frontdesk: 0, journeys: 0 })
    cur.add(1, unit)
  }
  const put = (at, key, n = 1) => {
    const t = time(at)
    if (t == null || t < start || t > end) return
    const b = buckets.find(x => t >= x.from && t < x.to)
    if (b) b[key] += n
  }
  const inCampaign = campaignOfSend(groups)
  sends.forEach(s => put(s.at, inCampaign[s.id] ? 'campaigns' : 'sends', Number(s.stats.bookings) || 0))
  if (frontDesk) frontDeskResults(frontDesk, { start: new Date(start), end: new Date(end) }).bookedAt.forEach(at => put(at, 'frontdesk'))
  if (journeys) journeyResults(journeys.list, journeys.runs, { start: new Date(start), end: new Date(end) }).forEach(j => j.bookedAt.forEach(at => put(at, 'journeys')))
  return { weekly, buckets: buckets.map(b => ({ ...b, total: b.campaigns + b.sends + b.frontdesk + b.journeys })) }
}

// What brought in most, across everything, best first
export const whatWorked = ({ sends = [], groups = null, posts = [], frontDesk = null, journeys = null, vouchers = null }, range, max = 5) => {
  const out = []
  campaignResults(groups, sends, posts, range).forEach((c) => { if (c.bookings) out.push({ key: `c:${c.id}`, kind: 'campaign', id: c.id, title: c.name, sub: 'Campaign', bookings: c.bookings }) })
  const inCampaign = campaignOfSend(groups)
  sends.filter(s => inRange(s.at, range) && !inCampaign[s.id] && Number(s.stats.bookings) > 0)
    .forEach(s => out.push({ key: `s:${s.id}`, kind: s.kind, id: s.id, title: s.name, sub: s.kind === 'sms' ? 'Text' : 'Email', bookings: Number(s.stats.bookings) }))
  if (journeys) journeyResults(journeys.list, journeys.runs, range).forEach((j) => { if (j.booked) out.push({ key: `j:${j.id}`, kind: 'journey', id: j.id, title: j.name, sub: `Journey · ${j.entered} ${j.entered === 1 ? 'client' : 'clients'} in`, bookings: j.booked }) })
  if (frontDesk) {
    const f = frontDeskResults(frontDesk, range)
    if (f.bookings) out.push({ key: 'fd', kind: 'frontdesk', title: 'Front desk', sub: `${f.chats} ${f.chats === 1 ? 'chat' : 'chats'}, ${f.calls} ${f.calls === 1 ? 'call' : 'calls'}`, bookings: f.bookings })
  }
  out.sort((a, b) => b.bookings - a.bookings)
  const list = out.slice(0, max)
  if (vouchers && vouchers.revenue > 0 && list.length < max) {
    list.push({ key: 'v', kind: 'vouchers', title: 'Gift vouchers', sub: `${vouchers.online.count || 0} online, ${vouchers.pos.count || 0} at the till`, money: vouchers.revenue })
  }
  return list
}
